import React, { useState } from 'react';
import {
    DatePicker, Input, Button, Table, Card, Form, Row, Col,
    message, Typography, Descriptions, Statistic, ConfigProvider, Tag
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { fetchReport } from '../../api/reportApi';
import { Order } from '../../types/order';
import { ReportData } from '../../types/report';

const { RangePicker } = DatePicker;
const { Title, Link, Text } = Typography;

const highlightText = (text: string | null | undefined, keyword: string) => {
    if (!text) return '';
    if (!keyword) return text;
    const parts = text?.split?.(new RegExp(`(${keyword})`, 'gi'));
    return (
        <>
            {parts?.map?.((part, i) =>
                part.toLowerCase() === keyword.toLowerCase() ? (
                    <Text mark key={i}>{part}</Text>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </>
    );
};

const statusColorMap: Record<string, string> = {
    processing: 'blue',
    ordered: 'gold',
    shipped: 'cyan',
    inStore: 'purple',
    delivered: 'green'
};

const Report: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const [form] = Form.useForm();
    const [data, setData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [searchText, setSearchText] = useState('');

    const currency = t('currencySymbol');
    const formatCurrency = (value: number) => `${Number(value).toLocaleString()} ${currency}`;

    const handleSearch = async () => {
        const values = await form.validateFields();
        const [start, end] = values.dateRange || [];

        if (!start || !end) return message.error(t('selectDateRange'));

        try {
            setLoading(true);
            const result = await fetchReport(
                start.format('YYYY-MM-DD'),
                end.format('YYYY-MM-DD'),
                values.customerCode
            );
            setData(result);
        } catch {
            message.error(t('fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const handleExpand = (expanded: boolean, record: Order) => {
        setExpandedRowKeys(expanded ? [record.id] : []);
    };

    const columns: ColumnsType<Order> = [
        {
            title: t('orderId'),
            dataIndex: 'id',
            key: 'id',
            render: (value) => highlightText(value, searchText),
            width: 180
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (value) => <Tag color={statusColorMap[value] || 'default'}>{highlightText(value, searchText)}</Tag>,
            width: 120
        },
        {
            title: t('totalPrice'),
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            render: (v) => highlightText(formatCurrency(v), searchText),
            width: 140
        },
        {
            title: t('paid'),
            dataIndex: 'pay',
            key: 'pay',
            render: (v) => highlightText(formatCurrency(v), searchText),
            width: 140
        },
        {
            title: t('remain'),
            dataIndex: 'remain',
            key: 'remain',
            render: (v) => highlightText(formatCurrency(v), searchText),
            width: 140
        },
        {
            title: t('createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (d: string) => highlightText(dayjs(d).format('YYYY-MM-DD HH:mm'), searchText),
            width: 180
        }
    ];

    const expandedRowRender = (record: Order) => (
        <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
            <Descriptions title={t('customerInfo')} bordered size="small" column={1}>
                <Descriptions.Item label={t('customerCode')}>
                    {highlightText(record.customerCode || '', searchText)}
                </Descriptions.Item>
                <Descriptions.Item label={t('cardLink')}>
                    <Link href={record.cardLink} target="_blank">
                        {highlightText(record.cardLink || '', searchText)}
                    </Link>
                </Descriptions.Item>
                <Descriptions.Item label={t('updatedAt')}>
                    {dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
            </Descriptions>

            <br />

            <Title level={5}>{t('items')}</Title>
            <Row gutter={[16, 16]}>
                {record.items?.map((item, idx) => (
                    <Col key={idx} xs={24} sm={12} md={8} lg={6}>
                        <Card
                            size="small"
                            title={`${t('qty')}: ${item.qty}`}
                            headStyle={{ backgroundColor: '#f0f5ff' }}
                            bodyStyle={{ padding: 10 }}
                            style={{ marginBottom: 16 }}
                        >
                            <img src={item.image} alt="item" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4 }} />
                            <p style={{ marginBottom: 4 }}><strong>{t('note')}:</strong> {highlightText(item.note || '', searchText)}</p>
                            <p style={{ marginBottom: 0 }}><strong>{t('detail')}:</strong> {highlightText(item.customization?.detail || '', searchText)}</p>
                        </Card>
                    </Col>
                ))}
            </Row>

            <br />

            <Title level={5}>{t('orderDetails')}</Title>
            <Table
                dataSource={record.orderDetails || []}
                pagination={false}
                size="small"
                rowKey={(_, i) => `od-${i}`}
                columns={[
                    {
                        title: t('orderNo'),
                        dataIndex: 'orderNo',
                        key: 'orderNo',
                        render: (value) => highlightText(value || '', searchText)
                    },
                    {
                        title: t('trackNo'),
                        dataIndex: 'orderTrackNo',
                        key: 'orderTrackNo',
                        render: (value) => highlightText(value || '', searchText)
                    }
                ]}
                scroll={{ x: true }}
            />
        </div>
    );

    const filteredOrders = data?.orders?.filter((order) => {
        const text = searchText.toLowerCase();
        return (
            order.id?.toLowerCase().includes(text) ||
            order.status?.toLowerCase().includes(text) ||
            order.customerCode?.toLowerCase().includes(text) ||
            order.totalPrice?.toString().includes(text) ||
            order.pay?.toString().includes(text) ||
            order.remain?.toString().includes(text) ||
            order.cardLink?.toLowerCase().includes(text) ||
            order.items?.some(item =>
                item.note?.toLowerCase().includes(text) ||
                item.customization?.detail?.toLowerCase().includes(text)
            ) ||
            order.orderDetails?.some(detail =>
                detail.orderNo?.toLowerCase().includes(text) ||
                detail.orderTrackNo?.toLowerCase().includes(text)
            )
        );
    }) || [];

    return (
        <ConfigProvider direction={isRTL ? 'rtl' : 'ltr'}>
            <div
                style={{
                    padding: '16px 8px',
                    background: '#fff',
                    borderRadius: 8,
                    overflowX: 'auto',
                    maxWidth: 1300,
                    margin: 'auto'
                }}
            >
                {/* Responsive filter form */}
                <Form
                    layout="inline"
                    form={form}
                    onFinish={handleSearch}
                    style={{
                        marginBottom: 16,
                        flexWrap: 'wrap',
                        display: 'flex',
                        gap: 12
                    }}
                >
                    <Form.Item
                        name="dateRange"
                        rules={[{ required: true }]}
                        style={{ minWidth: 220, flex: '1 1 220px', marginBottom: 8 }}
                    >
                        <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="customerCode"
                        style={{ minWidth: 180, flex: '1 1 180px', marginBottom: 8 }}
                    >
                        <Input placeholder={t('customerCode') as string} />
                    </Form.Item>
                    <Form.Item style={{ minWidth: 120, flex: '1 1 120px', marginBottom: 8 }}>
                        <Button type="primary" htmlType="submit" loading={loading} style={{ width: '100%' }}>
                            {t('search')}
                        </Button>
                    </Form.Item>
                </Form>

                {/* Responsive search input */}
                <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}>
                    <Input.Search
                        placeholder={t('search') as string}
                        allowClear
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ maxWidth: 350, width: '100%', minWidth: 150 }}
                    />
                </div>

                {/* Responsive Table */}
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Table
                        columns={columns}
                        dataSource={filteredOrders}
                        loading={loading}
                        rowKey="id"
                        expandable={{
                            expandedRowRender,
                            expandedRowKeys,
                            onExpand: handleExpand
                        }}
                        scroll={{ x: 'max-content' }}
                        pagination={{ pageSize: 5 }}
                        bordered
                        size="middle"
                    />
                </div>

                {/* Responsive summary */}
                {data && (
                    <Card title={t('summary')} style={{ marginTop: 24, background: '#f6ffed' }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic title={t('totalOrders')} value={data.totalOrders} />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic title={t('totalPaid')} value={data.totalPaid} suffix={currency} />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic title={t('totalRemaining')} value={data.totalRemaining} suffix={currency} />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Statistic title={t('totalOverallPrice')} value={data.totalPrice} suffix={currency} />
                            </Col>
                        </Row>
                    </Card>
                )}
            </div>
        </ConfigProvider>
    );
};

export default Report;
